import React, {useContext} from 'react';
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {GisContext} from "../context/gis-context.jsx";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../utils/enums/permission-enum.js";
import gisService from "../../../../services/web/gis-service.js";
import GisUtils from "../utils/gis-utils.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {Card} from "../../../../components/card/card.jsx";
import {
    FaBatteryThreeQuarters,
    FaBell,
    FaCheck,
    FaCopy,
    FaExclamation,
    FaExternalLinkAlt,
    FaPlay,
    FaQuestion,
    FaTimes,
    FaVideo
} from "react-icons/fa";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import {WazeSubType, WazeType} from "../../../../utils/waze-types.js";
import {FaMapLocationDot} from "react-icons/fa6";
import axios from "axios";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;
const API_URL = import.meta.env.VITE_API_URL;

const GisCardDetails = ({event, markers, map, overlay}) => {
    const {setModal, coreLib, geometryLib, circleRange, sentryGrid} = useContext(GisContext);

    const navigate = navigateHook();
    const hasPermission = useHasPermission();

    const hasDispatchPermission = hasPermission(PermissionEnum.DISPATCH_GARRISON_VEHICLE);

    const openCameraVms = (idCamera, vmsConfigurationId) => {
        const vmsCameraId = idCamera.match(/(\d+)$/)[0];

        const vmsTab = window.open("", "_blank");
        vmsTab.opener = null;
        axios.get(`${API_URL}/vms/access-from-gis/${vmsConfigurationId}/${vmsCameraId}`)
            .then(({data}) => vmsTab.location.href = data)
            .catch(() => {
                vmsTab.close();
                projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS.", "Ok");
            })
    }

    const calculateDatesLastStatus = (date) => {
        const dateInitial = new Date(date);
        const now = new Date();
        const difference = now.getTime() - dateInitial.getTime();
        const secondsDifference = Math.floor((difference) / (1000));

        const day = Math.floor(secondsDifference / (3600 * 24));
        const hour = Math.floor(secondsDifference / 3600);
        const minute = Math.floor(secondsDifference % 3600 / 60);
        const seconds = Math.floor(secondsDifference % 3600 % 60);

        const dayDisplay = day > 0 ? day + 'd' : '';
        const hourDisplay = hour > 0 ? hour + 'h' : '';
        const minuteDisplay = minute > 0 ? minute + 'min' : '';
        const secondsDisplay = seconds > 0 ? seconds + 'seg' : '';

        if (dayDisplay !== '') return dayDisplay;
        if (hourDisplay !== '') return hourDisplay;
        if (minuteDisplay !== '') return minuteDisplay;
        if (secondsDisplay !== '') return secondsDisplay;
    }

    const openGoogleMaps = (lat, lng) => {
        window.open(`https://www.google.com/maps/search/?api=1&query=${lat}%2C${lng}`, 'blank_');
    }

    const openWazeMap = (lat, lng) => {
        window.open(`https://www.waze.com/partnerhub/map-tool?lon=${lng}&lat=${lat}`, 'blank_');
    }

    const getMousePosition = (element) => {
        const x = element.nativeEvent.layerX;
        const y = element.nativeEvent.layerY;

        const point= new coreLib.Point(x, y);
        return overlay.getProjection().fromContainerPixelToLatLng(point);
    }

    const getAttendancesInRange = (position,) => {
        const attendances = [...markers].filter(([, marker]) => marker.type === "attendance" && marker.visible);
        const attendancesInRange = [];

        const zoom = map.getZoom();

        let radius;
        attendances.forEach(([, attendance]) => {
            const attendancesLatLng = new coreLib.LatLng(attendance.latitude, attendance.longitude);
            const distance = geometryLib.spherical.computeDistanceBetween(position, attendancesLatLng);

            switch (true) {
                case (zoom > 20 && distance < 5):
                    attendancesInRange.push(attendance);
                    radius = 5;
                    break;
                case ((zoom > 17 && zoom <= 20) && distance < 25):
                    attendancesInRange.push(attendance);
                    radius = 25;
                    break;
                case ((zoom <= 17 && zoom > 14) && distance < 200):
                    attendancesInRange.push(attendance);
                    radius = 200;
                    break;
                case (zoom <= 14 && distance < 700):
                    attendancesInRange.push(attendance);
                    radius = 700;
                    break;
                default:
                    circleRange.current.setVisible(false);
                    break;
            }
        });

        return { attendancesInRange, radius };
    };

    const dragGarrison = (element) => {
        const position = getMousePosition(element);

        const {attendancesInRange, radius} = getAttendancesInRange(position);

        if (attendancesInRange.length) {
            circleRange.current.setOptions({
                center: attendancesInRange[0].getPosition(),
                radius,
                visible: true
            });
        }
    }

    const dropGarrison = (element) => {
        const position = getMousePosition(element);

        const { attendancesInRange } = getAttendancesInRange(position);

        if (attendancesInRange.length) {
            const attendancesIds = attendancesInRange.map(item => item.id.split("-")[1]);

            const model = {
                attendancesIds: attendancesIds,
                garrisonId: element.target.getAttribute("garrison-id").split("-")[1]
            }

            gisService.dispatchByAttendanceId(model)
                .then((data) => {
                    const formated = attendancesInRange.map(item => {
                        const attendanceId = parseInt(item.id.split("-")[1]);
                        const find = data.find(value => value.attendance_id === attendanceId);

                        item.details.support = !!find;
                        item.details.dispatch_activity = !!find?.dispatch_activity;
                        item.details.garrison_support_id = find?.garrison_id ?? null;

                        return item;
                    });

                    setModal({
                        attendances: formated,
                        event,
                        show: true
                    });
                });
        }
    }

    const getDetailsInfo = () => {
        if (!event) return;

        let div;
        const {title, type, details, icon, garrisonType, date, description} = event;

        switch (type) {
            // TODO: adaptar esse trecho para usar o componente CarrisonCard usado no despacho
            case "attendance":
                const {garrisonIdentification, protocol, priority, vehiclePlate} = details;
                const iconVehicleTypeAttendance = GisUtils.getIconVehicleType(details.vehicleType);
                const lastStatusAttendance = date ? calculateDatesLastStatus(date) : '';

                div = (
                    <React.Fragment>
                        <div className="attendance-card">
                            <div className={"attendances-card " + icon}>
                                <div className={"panel-heading-card " + icon}></div>

                                <div className={"panel-title-card " + icon}>
                                    <div className="flex-card">
                                        <div className="card-panel-body pull-left title-card-attendance">
										<span className="flex items-center gap-1">
											<span>
                                                <b>{title ?? details.nature}</b>
                                            </span>
										</span>
                                        </div>
                                        <div className="card-panel-body">
                                            <span className="float-end">{lastStatusAttendance}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={"attendances-panel-body " + icon}>
                                    <div className="flex-card">
                                        <div className="pull-left card-panel-body w-full">
                                            <div className="code-card-priority w-full" style={{"color": "#ff7b00"}}>
                                                <b className="float-end">{priority}</b>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-card">
                                        <div className="float-end card-panel-body">
                                            <span style={{"marginRight": "2px", "userSelect": "none"}}>
                                                {garrisonIdentification ? iconVehicleTypeAttendance : ""}
                                            </span>

                                            {garrisonIdentification} {vehiclePlate ? (" | " + vehiclePlate) : ""}
                                        </div>
                                        <div className="pull-left card-panel-body">
                                            <span className="font-number-card float-end">{protocol}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                );
                break;
            case "garrison":
                // TODO: adaptar esse trecho para usar o componente AttendanceCard usado no despacho
                const {nature, battery, contingentName, main, phone, vehicleType, intendedFunction} = details;
                const lastStatus = date ? calculateDatesLastStatus(date) : '';

                const iconVehicleType = GisUtils.getIconVehicleType(vehicleType);
                const draggable = hasDispatchPermission && (garrisonType === "available" || garrisonType === "dispatchActivity");

                circleRange.current.setMap(map);

                div = (
                    <React.Fragment>
                        <div className="garrisons-card h-auto!">
                            <div className={"garrisons-card " + garrisonType} draggable={draggable} onDrag={dragGarrison} onDragEnd={dropGarrison} garrison-id={event.id}>
                                <div className={"panel-heading-card " + garrisonType}></div>

                                <div className={"panel-title-card " + garrisonType}>
                                    <div className="flex-card">
                                        <div className="card-panel-body title-card-garrison">
                                            <b><span>{nature ? nature : intendedFunction}</span></b>
                                        </div>
                                    </div>
                                </div>
                                <div className={"garrisons-cards-body " + garrisonType}>
                                    <div className="flex-card mb-1">
                                        {
                                            battery !== "" && battery !== undefined &&
                                            <div className="card-panel-body">
                                                <FaBatteryThreeQuarters /> {battery}%
                                            </div>
                                        }
                                        <div className={"card-panel-body " + (battery !== "" && battery !== undefined ? "" : "w-full")}>
                                            {
                                                date &&
                                                <span className="float-end">
                                                     Último status há <span className="font-bold"> {lastStatus || '0seg'}</span>
                                                </span>
                                            }
                                        </div>
                                    </div>
                                    <div className="flex-card mb-1">
                                        <div className="card-panel-body">
                                            <span className="font-bold">{main}</span>
                                        </div>
                                        <div className="card-panel-body">
                                            <span className="float-end">{projectUtils.formatPhone(phone)}</span>
                                        </div>
                                    </div>

                                    <div className="flex-card">
                                        <div className="card-panel-body">
                                            <span style={{"marginRight": "2px"}}>
                                                {iconVehicleType}
                                            </span>
                                            <p className={"truncate"}>{contingentName}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {
                            draggable && <small>* Arraste o card até um atendimento para poder despachar.</small>
                        }
                    </React.Fragment>
                );
                break;
            case "sos":
                const {dtStart, protectionTypes, eventId} = details;

                div = (
                    <React.Fragment>
                        <Card className="card default-card mb-2">
                            <Card.Header>
                                <Card.Title className={"!text-[13px]"}><b>{title}</b></Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <p>Proteção: <b>{protectionTypes}</b></p>
                                {
                                    details.description &&
                                    <p>Descrição: <b>{details.description}</b></p>
                                }
                                <p className="mb-2">D/H do acionamento: <b>{new Date(dtStart).toLocaleString()}</b></p>
                                {
                                    !sentryGrid &&
                                    <span onClick={() => navigate(`/sos/event-open/${eventId}`, true)}
                                          className="mt-2 flex gap-2 cursor-pointer">
                                        <FaExternalLinkAlt /> Abrir SOS
                                    </span>
                                }
                            </Card.Body>
                        </Card>
                    </React.Fragment>
                );

                break;
            case "janitorial":
                const {reason, dtIni, name, createUser} = details;
                const openSince = dtIni ? calculateDatesLastStatus(new Date(dtIni).toISOString()) : '';

                div = (
                    <React.Fragment>
                        <Card className="card default-card mb-2">
                            <Card.Header className="card-header">
                                <Card.Title className="!text-[13px]"><b>{reason}</b></Card.Title>
                            </Card.Header>
                            <Card.Body className="card-body">
                                <p>Aberto há: <b>{openSince}</b></p>
                                <p className="mb-2">Cadastrado por: <b>{createUser}</b></p>

                                {
                                    !sentryGrid &&
                                    <span className={"flex gap-2 cursor-pointer"} onClick={() => navigate(`/janitorial/form/${name}`, true)}>
                                        <FaExternalLinkAlt /> Abrir zeladoria
                                    </span>
                                }
                            </Card.Body>
                        </Card>
                    </React.Fragment>
                );

                break;
            case "capturePlace":
                let lanes;
                if (details) {
                    lanes = details.map(lane => {
                        const statusIcon = {
                            ONLINE: <FaCheck />,
                            OFFLINE: <FaTimes />,
                            WARNING: <FaExclamation />,
                            LANE_NOT_CONFIG: <FaQuestion />,
                        }

                        const laneTitle = lane.title.replace(title, "").replace("(", "").replace(")", "");

                        return (
                            <div className="capture-place-lane" key={lane.id}>
                                <span className={lane.status}>
                                    {statusIcon[lane.status]}
                                </span>
                                <p className="truncate mb-0" title={laneTitle}>{laneTitle}</p>
                                {
                                    lane.alarm &&
                                    <FaBell color={"red"} />
                                }
                            </div>
                        )
                    });
                }

                div = (
                    <Card className="default-card mb-2">
                        <Card.Header>
                            <Card.Title className={"flex justify-between w-full !text-[13px]"}>
                                <b>{title}</b>
                                {
                                    event.alarm &&
                                    <FaBell color={"red"}/>
                                }
                            </Card.Title>
                        </Card.Header>
                        <Card.Body className="body-noc">
                            {lanes}
                        </Card.Body>
                    </Card>
                );
                break;
            case "vms":
                const vmsConfigurationId = details?.vmsConfigurationId;
                const streetVms = details?.description?.street;
                const numberVms = details?.description?.number;
                const neighborhoodVms = details?.description?.neighborhood;
                const cityVms = details?.description?.city;
                const vmsConfigurationName = details?.vmsConfigurationName;

                let cameraId = details?.idCamera;
                cameraId = typeof cameraId === "number"
                    ? cameraId
                    : parseInt(cameraId.match(/(\d+)$/)?.[0] ?? "", 10) || null;

                div = (
                    <Card className="default-card mb-2">
                        <Card.Header>
                            <Card.Title className={"!text-[13px]"}>
                                <b>{title}</b>
                            </Card.Title>
                        </Card.Header>
                        <Card.Body style={{height: '213px'}}>
                            <div className="thumbnail">
                                {/*Vigia Mais*/}
                                {
                                    vmsConfigurationId &&
                                    <>
                                        <div className="play vms" onClick={() => playVideo(cameraId, title, vmsConfigurationId)}>
                                            <FaPlay color={"#fff"} size={18}/>
                                        </div>
                                        <div className="img">
                                            <img
                                                src={`${API_KEEPER}/vms/cameras/thumbnail/${cameraId}/${vmsConfigurationId}`}
                                                onError={(e) => e.target.src = getAssetPath('img/img-not-found.png')}
                                                alt="" className={"vms-thumbnail"}/>
                                        </div>
                                    </>
                                }

                            </div>

                            {
                                (!sentryGrid && vmsConfigurationId) &&
                                <div className={"mt-2 mb-2"} >
                                    <a className={"flex gap-1 cursor-pointer hover:underline"} onClick={() => openCameraVms(details.idCamera, vmsConfigurationId)} target='_blank'>
                                        <FaExternalLinkAlt /> Ver vídeo gravado - {vmsConfigurationName}
                                    </a>
                                </div>
                            }
                            <div className="address">
                                <p className="truncate mb-0">
                                    {streetVms} {streetVms && numberVms ? `- ${numberVms}` : numberVms}
                                </p>
                                <p className="truncate mb-0">
                                    {neighborhoodVms} {neighborhoodVms && cityVms ? `- ${cityVms}` : ''}
                                </p>
                            </div>
                        </Card.Body>
                    </Card>
                )
                break;
            case "contactid":
                const {status, connection, id, dvrEnable} = details;

                div = (
                    <React.Fragment>
                        <Card className="default-card mb-2">
                            <Card.Header>
                                <Card.Title className={"flex justify-between w-full !text-[13px]"}>
                                    <b>{title}</b>
                                    {
                                        event.alarm &&
                                        <FaBell color={"red"}/>
                                    }
                                </Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <p>Conexão: <b>{connection}</b></p>
                                <p>Status: <b>{status}</b></p>

                                {
                                    (dvrEnable && !sentryGrid) &&
                                    <span className="cursor-pointer mt-1 flex gap-2" onClick={() => projectUtils.openDvr(id)}>
                                        <FaVideo /> Abrir DVR
                                    </span>
                                }
                            </Card.Body>
                        </Card>
                    </React.Fragment>
                );

                break;
            case "point":
                const { address = '', number = '', neighborhood = '', city = '', rtsp, observation } = description;

                const addressString = [
                    address,
                    number && (address ? ", " : "") + number,
                    neighborhood && (address || number ? " - " : "") + neighborhood,
                    city && (address || number || city ? ", " : "") + city
                ].filter(Boolean).join('');

                div = (
                    <Card className="default-card mb-2">
                        <Card.Header>
                            <Card.Title className={"!text-[13px]"}><b>{title}</b></Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <p>Endereço: <b>{addressString}</b></p>
                            <p className="mb-2">Observação: <b>{observation}</b></p>

                            {
                                !sentryGrid &&
                                <span className="cursor-pointer flex gap-2" onClick={() => openGoogleMaps(event.latitude, event.longitude)}>
                                    <FaMapLocationDot /> Abrir no Google Maps
                                </span>
                            }
                        </Card.Body>
                    </Card>
                );
                break;
            case "waze":
                const {type, subType, street} = details;

                div = (
                    <Card className="default-card mb-2">
                        <Card.Header>
                            <Card.Title className={"!text-[13px]"}>
                                <b>{WazeType[type] + (subType ? " - " + WazeSubType[subType] : "")}</b>
                            </Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-2">Endereço: <b>{street}</b></p>
                            {
                                !sentryGrid &&
                                <span className="cursor-pointer flex gap-2" onClick={() => openWazeMap(event.latitude, event.longitude)}>
                                    <FaExternalLinkAlt /> Abrir no Waze Partner Hub
                                </span>
                            }
                        </Card.Body>
                    </Card>
                );
                break;
            case "tracker":
                const {velocidade, ignicao, hodometro} = details;
                const hodometroFormated = (parseInt(hodometro)).toLocaleString(undefined, {minimumFractionDigits: 0});
                const lastStatusDate = event.date ? calculateDatesLastStatus(event.date) : '';

                div = (
                    <Card className="default-card mb-2">
                        <Card.Header className="!text-[13px]">
                            <Card.Title style={{"fontSize": "13px"}}><b>{title}</b></Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <p>Velocidade: <b>{velocidade} km/h</b></p>
                            <p>Ignição:
                                <b>
                                    {ignicao ?
                                        <FaCheck color={"#198754"} /> :
                                        <FaTimes color={"#dc4c64"}/>
                                    }
                                </b>
                            </p>
                            <p>Hodômetro: <b>{hodometroFormated} kms.</b></p>
                            <small className="tracker-date">Último status há {lastStatusDate || ''}</small>
                        </Card.Body>
                    </Card>
                );
                break;
            case "weather":
                const {rain, rainDay, temperatura, uv, winb, wind} = details;
                const lastStatusDate1 = event.date ? calculateDatesLastStatus(event.date) : '';

                div = (
                    <Card className="default-card mb-2">
                        <Card.Header>
                            <Card.Title className={"!text-[13px]"}><b>{title}</b></Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <p>Sensação térmica: <b>{temperatura}°C</b></p>
                            <p>UV: <b>{uv}</b></p>
                            <p>Rajadas: <b>{winb} km/h</b></p>
                            <p>Vento médio: <b>{wind} km/h</b></p>
                            <p>Probabilidade de Chuva: <b>{rainDay}%</b></p>
                            <p>Milímetros de chuva do dia: <b>{rain}mm</b></p>
                            <small className="tracker-date">Último status há {lastStatusDate1 || ''}</small>
                        </Card.Body>
                    </Card>
                );

                break;
        }

        return div;
    }

    const playVideo = (cameraId, cameraName, vmsId) => {
        GisUtils.openMosaicCameras([{id: cameraId, name:cameraName, vmsId}]);
    }

    return (
        <div id="information-div">
            <hr className={"text-[#bdbdbd]"}/>
            <h4 className="font-bold mb-3">
                Detalhes

                {
                    event &&
                    <span
                        className="cursor-pointer float-right"
                        title="Copiar localização"
                        onClick={() => projectUtils.copyToClipboard("https://www.google.com/maps/search/?api=1&query=" + event.latitude + "%2C" + event.longitude, "Latitude e longitude copiadas.")}>
                        <FaCopy />
                    </span>
                }
            </h4>

            {getDetailsInfo()}
        </div>
    );
};

export default GisCardDetails;
